import React, { useState } from 'react';
import { matchesSearch, noMatchText } from '../lib/pageSearch';
import { SovIcon, SovBandLabel, sovCardStyle, sovHeadStyle } from './sov/SovLayout';

/* Schedule of values from ComputerEase — read-only (PSR #69, Duncan 2026-10-17).
 * For a job billed in CE, the SOV tab shows what the CE Agent read off each AIA invoice's
 * continuation sheet (GET /api/ce-sov/project/:id): pick an application, see its lines; the
 * applications read so far are listed under it, a credit memo printed with an invoice's own
 * number right after that invoice. Nothing here edits — the lines are CE's, as read. */

const money = (v) => (v == null ? '—'
  : `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? '—' : `${Math.round(v * 10) / 10}%`);
const usDate = (d) => {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
  return `${m}/${dd}/${y}`;
};
const readOn = (ts) => {
  if (!ts) return null;
  const s = typeof ts === 'string' && !/(Z|[+-]\d\d:?\d\d)$/.test(ts) ? `${ts}Z` : ts;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
};
const memoName = (kind) => (kind === 'DM' ? 'Debit memo' : 'Credit memo');
const optionLabel = (a) => (a.kind === 'invoice'
  ? `Application ${a.application_number ?? '—'} · to ${usDate(a.period_to)}`
  : `${memoName(a.kind)} ${a.number} · ${usDate(a.application_date || a.period_to)}`);
/** [1,2,4] → "1–2, 4" */
const ranges = (ns) => {
  const out = [];
  ns.forEach((n) => {
    const last = out[out.length - 1];
    if (last && n === last[1] + 1) last[1] = n; else out.push([n, n]);
  });
  return out.map(([a, b]) => (a === b ? `${a}` : `${a}–${b}`)).join(', ');
};

const COLS = [
  { key: 'item', label: 'Item', align: 'left', w: '64px' },
  { key: 'description', label: 'Description', align: 'left', w: 'minmax(220px, 1fr)' },
  { key: 'scheduled', label: 'Scheduled value', w: '156px' },
  { key: 'previous', label: 'Previous', w: '124px' },
  { key: 'this_period', label: 'This period', w: '124px' },
  { key: 'stored', label: 'Stored', w: '104px' },
  { key: 'completed', label: 'Completed', w: '132px' },
  { key: 'percent', label: '%', w: '64px' },
  { key: 'balance', label: 'Balance', w: '124px' },
  { key: 'retainage', label: 'Retainage', w: '112px' },
];
const GRID = COLS.map((c) => c.w).join(' ');
const MIN_W = 1204;
const cell = (c) => ({
  padding: '0 10px', textAlign: c.align || 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
});
const numCell = { fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' };

function LineRow({ l, last }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', minWidth: MIN_W,
      padding: '9px 0', fontSize: 14, borderBottom: last ? 'none' : '1px solid var(--border-subtle)',
    }}>
      <div className="mono" style={{ ...cell(COLS[0]), color: 'var(--text-3)' }}>{l.item}</div>
      <div style={{ ...cell(COLS[1]), color: 'var(--text-1)' }} title={l.description}>{l.description || '—'}</div>
      {COLS.slice(2).map((c) => (
        <div key={c.key} className="mono" style={{
          ...cell(c), ...numCell, fontWeight: c.key === 'completed' ? 600 : 500,
          color: c.key === 'previous' ? 'var(--text-3)'
            : c.key === 'balance' && l.balance === 0 ? 'var(--success)'
              : c.key === 'retainage' && l.retainage > 0 ? 'var(--warning)'
                : (l[c.key] || 0) < 0 ? 'var(--danger)' : 'var(--text-1)',
        }}>{c.key === 'percent' ? pct(l.percent) : money(l[c.key])}</div>
      ))}
    </div>
  );
}

/* A FREEFORM invoice or memo (no continuation sheet: View Invoice renders it straight into Word),
 * shown as CE prints it — every line, then subtotal, tax, total. Lines that don't add up to CE's
 * subtotal are shown as printed, with a note (DEW-L-01 142696: one $3,904.00 line under a
 * $170,201.00 subtotal). */
const FF_GRID = '72px minmax(240px, 1fr) 140px 150px';
function FreeformDoc({ d, search, first }) {
  const shown = d.lines.filter((l) => matchesSearch(search, l.description));
  const r = { padding: '0 10px', textAlign: 'right', ...numCell };
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--border-default)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', padding: '11px 16px 8px' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>
          {d.kind === 'invoice' ? 'Invoice' : memoName(d.kind)} <span className="mono">{d.number}</span>
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>
          {usDate(d.date)}{d.terms ? ` · ${d.terms}` : ''}{d.sold_to ? ` · ${d.sold_to}` : ''}
        </span>
      </div>
      {d.lines_match_total === false && (
        <div style={{ margin: '0 16px 8px', fontSize: 13, color: 'var(--warning)' }}>
          The lines add up to {money(d.lines_total)}; CE's subtotal is {money(d.subtotal)}. Shown as CE prints it.
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: FF_GRID, minWidth: 620, padding: '8px 0', background: 'var(--bg-surface-2)',
          borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)',
          fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-3)',
        }}>
          <div style={{ padding: '0 10px', textAlign: 'right' }}>Qty</div>
          <div style={{ padding: '0 10px' }}>Description</div>
          <div style={{ padding: '0 10px', textAlign: 'right' }}>Unit price</div>
          <div style={{ padding: '0 10px', textAlign: 'right' }}>Amount</div>
        </div>
        {shown.length === 0 && d.lines.length > 0 && (
          <div style={{ minWidth: 620, padding: '14px 16px', fontSize: 13.5, color: 'var(--text-3)', borderBottom: '1px solid var(--border-subtle)' }}>{noMatchText(search).body}</div>
        )}
        {shown.map((l) => (
          <div key={l.line_no} style={{ display: 'grid', gridTemplateColumns: FF_GRID, minWidth: 620, padding: '8px 0', fontSize: 14, borderBottom: '1px solid var(--border-subtle)' }}>
            <div className="mono" style={{ ...r, color: 'var(--text-3)' }}>{l.quantity == null ? '' : Math.round(l.quantity * 100) / 100}</div>
            <div style={{ padding: '0 10px', color: 'var(--text-1)' }}>{l.description || '—'}</div>
            <div className="mono" style={{ ...r, color: 'var(--text-3)' }}>{l.unit_price == null ? '' : money(l.unit_price)}</div>
            <div className="mono" style={{ ...r, fontWeight: 600, color: (l.amount || 0) < 0 ? 'var(--danger)' : 'var(--text-1)' }}>{l.amount == null ? '' : money(l.amount)}</div>
          </div>
        ))}
        {[['Subtotal', d.subtotal], ['Tax', d.tax], ['Total', d.total]].filter(([, v]) => v != null).map(([label, v]) => (
          <div key={label} style={{ display: 'grid', gridTemplateColumns: FF_GRID, minWidth: 620, padding: '6px 0', fontSize: 14, fontWeight: label === 'Total' ? 600 : 500 }}>
            <div />
            <div style={{ padding: '0 10px', color: 'var(--text-2)' }}>
              {label}{label === 'Subtotal' && shown.length < d.lines.length ? ` · ${shown.length} of ${d.lines.length} line${d.lines.length === 1 ? '' : 's'} shown` : ''}
            </div>
            <div />
            <div className="mono" style={{ ...r, color: v < 0 ? 'var(--danger)' : 'var(--text-1)' }}>{money(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const BADGE = {
  fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
  color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
};
/* A section's header (sov/SovLayout.js): its icon, title and badge; the line under them; its count or totals at right. */
function SectionHead({ tone, icon, title, badge, sub, right, big }) {
  return (
    <div style={{ ...sovHeadStyle(tone), display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <SovIcon tone={tone} icon={icon} size={big ? 30 : 26} />
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: big ? 17 : 15.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{title}</span>
          {badge && <span style={BADGE}>{badge}</span>}
        </div>
        {sub && <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.45, textWrap: 'pretty' }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}
const headFigure = { fontSize: 13, color: 'var(--text-2)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

function FreeformCard({ docs, search }) {
  const total = Math.round(docs.reduce((s, d) => s + (Number(d.total) || 0), 0) * 100) / 100;
  return (
    <div style={sovCardStyle(false)}>
      <SectionHead tone="freeform" icon="receipt" title="Freeform invoices" badge="From ComputerEase · read-only"
        sub="These have no continuation sheet, so each one is shown as CE prints it."
        right={<span className="mono" style={headFigure}>{docs.length} · {money(total)}</span>} />
      <div style={{ overflow: 'hidden', borderRadius: '0 0 14px 14px' }}>
        {docs.map((d, i) => <FreeformDoc key={`${d.kind}|${d.number}`} d={d} search={search} first={i === 0} />)}
      </div>
    </div>
  );
}

/* The footing under the Schedule of Values: the pay applications read from CE and the Freeform invoices, side by side
   (one fills the row when the other isn't there). */
const FOOTING = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))', gap: 16, alignItems: 'start' };

export default function CeSovPanel({ data, search, onStartSummit }) {
  const apps = data.applications || [];
  const freeform = data.freeform || [];
  const first = Math.max(0, apps.findIndex((a) => a.kind === 'invoice'));
  const [sel, setSel] = useState(first);
  if (!apps.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {freeform.length > 0 && (
          <div>
            <SovBandLabel>Where the billing comes from</SovBandLabel>
            <FreeformCard docs={freeform} search={search} />
          </div>
        )}
        {onStartSummit && (
          <div style={{ fontSize: 13, color: 'var(--text-4)' }}>
            Moving this job into Summit?{' '}
            <button type="button" onClick={onStartSummit} style={{
              border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textDecoration: 'underline',
            }}>Start its SOV in Summit</button>
          </div>
        )}
      </div>
    );
  }
  const a = apps[Math.min(sel, apps.length - 1)];
  // The search bar at the top filters the lines shown, and the totals row adds up what's shown
  // (like a filtered spreadsheet); with no search it is the application's own totals.
  const shown = a.lines.filter((l) => matchesSearch(search, l.item, l.description, l.section));
  const totals = shown.length === a.lines.length ? a.totals : (() => {
    const t = {};
    ['scheduled', 'previous', 'this_period', 'stored', 'completed', 'balance', 'retainage'].forEach((k) => {
      t[k] = Math.round(shown.reduce((acc, l) => acc + (Number(l[k]) || 0), 0) * 100) / 100;
    });
    t.percent = t.scheduled ? Math.round((t.completed / t.scheduled) * 1000) / 10 : null;
    return t;
  })();

  // Lines in sheet order, with a section heading wherever the section changes.
  const blocks = [];
  shown.forEach((l) => {
    const prev = blocks[blocks.length - 1];
    if (!prev || prev.section !== (l.section || null)) blocks.push({ section: l.section || null, lines: [] });
    blocks[blocks.length - 1].lines.push(l);
  });
  const what = a.kind === 'invoice' ? `CE invoice ${a.number}` : `CE ${memoName(a.kind).toLowerCase()} ${a.number}`;
  const when = readOn(a.read_at);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* The main section: the Schedule of Values (sov/SovLayout.js) */}
      <div style={sovCardStyle(true)}>
        {/* Header: title, read-only badge, which document; its totals and the application picker */}
        <SectionHead big tone="sov" icon="table" title="Schedule of Values" badge="From ComputerEase · read-only"
          sub={<>Read from {what}{when ? ` on ${when}` : ''}. This job is billed in CE, so its SOV isn’t in Summit yet.</>}
          right={(
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <span className="mono" style={headFigure}>
                {a.lines.length} line{a.lines.length === 1 ? '' : 's'} · {money(a.totals.scheduled)} · {pct(a.totals.percent)} complete
              </span>
              <select aria-label="Application" value={sel} onChange={(e) => setSel(Number(e.target.value))} style={{
                height: 34, padding: '0 10px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)',
                background: 'var(--bg-surface)', border: '1px solid var(--border-default)', fontFamily: 'var(--font-sans)', cursor: 'pointer',
              }}>
                {apps.map((x, i) => <option key={`${x.kind}|${x.number}`} value={i}>{optionLabel(x)}</option>)}
              </select>
            </div>
          )} />

        {/* The continuation sheet */}
        <div style={{ overflowX: 'auto', borderRadius: '0 0 14px 14px' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: GRID, minWidth: MIN_W, padding: '11px 0',
            background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)',
            fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-3)',
          }}>
            {COLS.map((c) => <div key={c.key} style={cell(c)}>{c.label}</div>)}
          </div>
          {shown.length === 0 && a.lines.length > 0 && (
            <div style={{ minWidth: MIN_W, padding: '14px 16px', fontSize: 13.5, color: 'var(--text-3)', borderBottom: '1px solid var(--border-subtle)' }}>{noMatchText(search).body}</div>
          )}
          {blocks.map((b, bi) => (
            <React.Fragment key={bi}>
              {b.section && (
                <div style={{
                  minWidth: MIN_W, padding: '8px 10px', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.04em', color: 'var(--text-2)', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)',
                }}>{b.section}</div>
              )}
              {b.lines.map((l, li) => <LineRow key={`${bi}-${li}`} l={l} last={bi === blocks.length - 1 && li === b.lines.length - 1} />)}
            </React.Fragment>
          ))}
          <div style={{
            display: 'grid', gridTemplateColumns: GRID, minWidth: MIN_W, padding: '11px 0', fontSize: 14, fontWeight: 600,
            borderTop: '1px solid var(--border-default)', background: 'var(--bg-surface-2)',
          }}>
            <div style={cell(COLS[0])} />
            <div style={{ ...cell(COLS[1]), color: 'var(--text-1)' }}>
              Total · {shown.length < a.lines.length ? `${shown.length} of ${a.lines.length} lines` : `${a.lines.length} line${a.lines.length === 1 ? '' : 's'}`}
            </div>
            {COLS.slice(2).map((c) => (
              <div key={c.key} className="mono" style={{ ...cell(c), ...numCell, color: (totals[c.key] || 0) < 0 ? 'var(--danger)' : 'var(--text-1)' }}>
                {c.key === 'percent' ? pct(totals.percent) : money(totals[c.key])}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Where the billing comes from: every pay application read so far (click one to show it above) and the
          Freeform invoices, side by side under the Schedule of Values. */}
      <div style={{ marginTop: 28 }}>
      <SovBandLabel>Where the billing comes from</SovBandLabel>
      <div style={FOOTING}>
      <div style={sovCardStyle(false)}>
        <SectionHead tone="apps" icon="invoice" title="Pay applications read from ComputerEase"
          sub="Click one to show it in the Schedule of Values above."
          right={(() => {
            const n = apps.filter((x) => x.kind === 'invoice').length;
            const latest = apps.find((x) => x.kind === 'invoice');
            return (
              <span className="mono" style={headFigure}>
                {n}{latest ? ` · latest App ${latest.application_number ?? '—'}, to ${usDate(latest.period_to)}` : ''}
              </span>
            );
          })()} />
        <div style={{ overflow: 'hidden', borderRadius: '0 0 14px 14px' }}>
        {apps.map((x, i) => {
          const on = i === sel;
          return (
            <button key={`${x.kind}|${x.number}`} type="button" onClick={() => setSel(i)} style={{
              display: 'grid', gridTemplateColumns: '120px 90px 120px minmax(0, 1fr) 120px', gap: 10, alignItems: 'center',
              width: '100%', textAlign: 'left', padding: '9px 16px', fontSize: 13.5, cursor: on ? 'default' : 'pointer',
              border: 'none', borderTop: i ? '1px solid var(--border-subtle)' : 'none', fontFamily: 'var(--font-sans)',
              background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--accent-text)' : 'var(--text-2)',
            }}>
              <span style={{ fontWeight: 600 }}>{x.kind === 'invoice' ? `App ${x.application_number ?? '—'}` : memoName(x.kind)}</span>
              <span className="mono">{x.number}</span>
              <span>{x.kind === 'invoice' ? `to ${usDate(x.period_to)}` : usDate(x.application_date || x.period_to)}</span>
              <span className="mono" style={{ ...numCell, color: on ? 'inherit' : (x.totals.this_period < 0 ? 'var(--danger)' : 'var(--text-3)') }}>
                this period {money(x.totals.this_period)}
              </span>
              <span className="mono" style={{ textAlign: 'right', color: on ? 'inherit' : 'var(--text-3)' }}>
                {x.kind === 'invoice' && x.totals.percent != null ? `${pct(x.totals.percent)} complete` : ''}
              </span>
            </button>
          );
        })}
        {data.missing && data.missing.length > 0 && (
          <div style={{ padding: '9px 16px', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5, color: 'var(--text-4)' }}>
            App {ranges(data.missing)} · not read yet (Settings → Invoice Lines reads them)
          </div>
        )}
        </div>
      </div>

      {freeform.length > 0 && <FreeformCard docs={freeform} search={search} />}
      </div>
      </div>

      {onStartSummit && (
        <div style={{ fontSize: 13, color: 'var(--text-4)', marginTop: 20 }}>
          Moving this job into Summit?{' '}
          <button type="button" onClick={onStartSummit} style={{
            border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textDecoration: 'underline',
          }}>Start its SOV in Summit</button>
        </div>
      )}
    </div>
  );
}
